import React, { useState, useEffect } from "react"
import { useMutation } from "urql"

import { FilePicker } from "../../../../components/shared/FileStackWidgets"
import { Toggle } from "../../../../components/shared/Inputs"
import { useToast } from "../../../../contexts/ToastContext"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorSelector from "../shared/BackgroundColorSelector"
import ImagePickerV2 from "../shared/ImagePickerV2"

import EditorFooter from "./shared/EditorFooter"

// Layout options with shape border variants included in the main list
const layoutOptions = [
  {
    id: "full_image",
    name: "Full Image",
    description: "Full width background image with centered text overlay",
    image: require("../../../../../assets/images/builder/v2/layout-images/full-img.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/full-img-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/full-img-selected.png")
  },
  {
    id: "full_image_wave",
    name: "Wave",
    description: "Full width background image with wave shape border",
    image: require("../../../../../assets/images/builder/v2/layout-images/full-img-wave.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/full-img-wave-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/full-img-wave-selected.png")
  },
  {
    id: "full_image_point",
    name: "Point",
    description: "Full width background image with point shape border",
    image: require("../../../../../assets/images/builder/v2/layout-images/full-img-point.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/full-img-point-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/full-img-point-selected.png")
  },
  {
    id: "full_image_rounded",
    name: "Rounded",
    description: "Full width background image with rounded shape border",
    image: require("../../../../../assets/images/builder/v2/layout-images/full-img-round.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/full-img-round-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/full-img-round-selected.png")
  },
  {
    id: "full_image_slant",
    name: "Slant",
    description: "Full width background image with slant shape border",
    image: require("../../../../../assets/images/builder/v2/layout-images/full-img-slant.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/full-img-slant-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/full-img-slant-selected.png")
  },
  {
    id: "full_image_point_up_opacity",
    name: "Point Up",
    description: "Full width background image with point up opacity shape border",
    image: require("../../../../../assets/images/builder/v2/layout-images/full-img-point-up-opa.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/full-img-point-up-opa-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/full-img-point-up-opa-selected.png")
  },
  {
    id: "contained_image",
    name: "Contained Image",
    description: "Full width contained image with centered text overlay",
    image: require("../../../../../assets/images/builder/v2/layout-images/contained-img.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/contained-img-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/contained-img-selected.png")
  },
  {
    id: "image_left",
    name: "Image Left",
    description: "Image on the left, text on the right",
    image: require("../../../../../assets/images/builder/v2/layout-images/img-l.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/img-l-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/img-l-selected.png")
  },
  {
    id: "image_right",
    name: "Image Right",
    description: "Image on the right, text on the left",
    image: require("../../../../../assets/images/builder/v2/layout-images/img-r.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/img-r-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/img-r-selected.png")
  },
  {
    id: "half_image_left",
    name: "Half Image Left",
    description: "Half-width image on the left",
    image: require("../../../../../assets/images/builder/v2/layout-images/half-img-l.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/half-img-l-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/half-img-l-selected.png")
  },
  {
    id: "half_image_right",
    name: "Half Image Right",
    description: "Half-width image on the right",
    image: require("../../../../../assets/images/builder/v2/layout-images/half-img-r.png"),
    imageHover: require("../../../../../assets/images/builder/v2/layout-images/half-img-r-hover.png"),
    imageSelected: require("../../../../../assets/images/builder/v2/layout-images/half-img-r-selected.png")
  }
]

const LayoutOption = ({ option, isSelected, onSelect }) => {
  const [isHovered, setIsHovered] = useState(false)

  const getImageSource = () => {
    if (isSelected) {
      return option.imageSelected
    } else if (isHovered) {
      return option.imageHover
    } else {
      return option.image
    }
  }

  return (
    <div
      className="cursor-pointer transition-all"
      onClick={() => onSelect(option.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={option.name}>
      <img
        src={getImageSource()}
        alt={`${option.name} layout`}
        className="h-16 w-24 rounded-lg object-cover transition-all duration-200"
        onError={(e) => {
          console.warn(`Failed to load image: ${e.target.src}`) // eslint-disable-line no-console
          e.target.style.display = "none"
          e.target.nextSibling.style.display = "block"
        }}
      />
      {/* Fallback placeholder */}
      <div className="hidden h-16 w-24 items-center justify-center rounded-lg bg-gray-200">
        <div className="text-xs text-gray-500">{option.name}</div>
      </div>
    </div>
  )
}

const HeroEditor = ({ section, practice, onSave, onCancel, onDelete, isEditing = false, canDelete = true }) => {
  const {
    handleUpdateSectionPreview,
    clearPreview,
    practice: contextPractice,
    pages,
    currentPage
  } = useWebsiteBuilderV2()
  const practiceData = practice || contextPractice

  const [formData, setFormData] = useState({
    headline: section?.headline || "",
    subheadline: section?.subheadline || "",
    body: section?.body || "",
    imageUrl: section?.imageUrl || practice?.headerHeroImageUrl || "",
    settings: section?.settings || {
      layout: "full_image",
      textAlign: "center",
      headingAlign: "center",
      subheadingAlign: "center",
      ctaAlign: "center",
      overlayOpacity: 30,
      showCTA: true,
      ctaText: "Learn More",
      ctaAction: "page",
      ctaTarget: "",
      showReviews: true,
      colorMode: "white",
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      // Font settings
      headlineSize: "40px",
      headlineColor: "#FFFFFF",
      subheadlineSize: "20px",
      subheadlineColor: "#FFFFFF",
      // CTA button spacing
      ctaTopMargin: 24,
      // Multiple images support
      images: [],
      // Shape border for full-image layouts
      shapeBorder: "none"
    }
  })
  const [errors, setErrors] = useState({})

  const formatServiceTargetValue = (serviceId) => `service:${serviceId}`
  const formatEventTargetValue = (eventTypeId) => `event:${eventTypeId}`
  const normalizeCtaTargetForSelect = (ctaTarget) => {
    if (!ctaTarget) return ""

    const target = String(ctaTarget)
    if (target.startsWith("service:") || target.startsWith("event:")) {
      return target
    }

    // Legacy values: service/package IDs were stored raw, event types as slug.
    if (/^\d+$/.test(target)) {
      return formatServiceTargetValue(target)
    }

    const eventType = (practiceData?.bookableEventTypes || []).find((item) => item.slug === target)
    if (eventType?.id) {
      return formatEventTargetValue(eventType.id)
    }

    return ""
  }

  // GraphQL mutation for uploading images
  const UPLOAD_IMAGE_MUTATION = `
    mutation UploadImage($handle: String!, $description: String!, $provider: String) {
      uploadImage(handle: $handle, description: $description, provider: $provider) {
        result
        errors
        photoUrl
        altText
      }
    }
  `
  const [{ fetching: uploadFetching }, uploadImage] = useMutation(UPLOAD_IMAGE_MUTATION)
  const { showToast } = useToast()

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "",
        subheadline: section.subheadline || "",
        body: section.body || "",
        imageUrl: section.imageUrl || practiceData?.headerHeroImageUrl || "",
        settings: {
          layout: "full_image",
          textAlign: "center",
          headingAlign: "center",
          subheadingAlign: "center",
          ctaAlign: "center",
          overlayOpacity: 30,
          showCTA: true,
          ctaText: "Learn More",
          ctaAction: "page",
          ctaTarget: "",
          showReviews: true,
          colorMode: section?.settings?.colorMode || "white",
          showBackgroundColor: section?.settings?.showBackgroundColor ?? false,
          backgroundColor: section?.settings?.backgroundColor || "#ffffff",
          // Font settings
          headlineSize: "40px",
          headlineColor: "#FFFFFF",
          subheadlineSize: "20px",
          subheadlineColor: "#FFFFFF",
          // CTA button spacing
          ctaTopMargin: 24,
          // Shape border
          shapeBorder: "none",
          ...section.settings
        }
      })
    }
  }, [section, practiceData])

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value }

      // Update preview immediately for content changes
      if (section?.id && ["headline", "subheadline", "body", "imageUrl"].includes(field)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...updated
        })
      }

      return updated
    })

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        settings: { ...prev.settings, [key]: value }
      }

      // Update preview immediately for layout changes and other important settings
      // Use the NEW values directly to avoid stale state issues
      if (
        section?.id &&
        [
          "layout",
          "textAlign",
          "headingAlign",
          "subheadingAlign",
          "ctaAlign",
          "overlayOpacity",
          "showCTA",
          "ctaText",
          "ctaAction",
          "ctaTarget",
          "showReviews",
          "colorMode",
          "showBackgroundColor",
          "backgroundColor",
          "headlineSize",
          "headlineColor",
          "subheadlineSize",
          "subheadlineColor",
          "ctaTopMargin",
          "shapeBorder",
          "useHomepageHero"
        ].includes(key)
      ) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...updatedFormData,
          settings: updatedFormData.settings
        })
      }

      return updatedFormData
    })
  }

  // Close button - discard changes and close immediately
  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.headline.trim()) {
      newErrors.headline = "Headline is required"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (validateForm()) {
      onSave({
        layout: "hero",
        ...formData
      })
    }
  }

  // Handle image selection from ImagePickerV2
  const handleImageSelect = ({ handle, provider }) => {
    uploadImage({
      handle: handle,
      description: "header_hero_image",
      provider: provider
    }).then((result) => {
      if (result?.data?.uploadImage?.result === "success") {
        handleChange("imageUrl", result.data.uploadImage.photoUrl)
        showToast("Image uploaded successfully!")
      } else {
        console.error(result) // eslint-disable-line no-console
        let errorMessage = "There was an error uploading your image. Please try again later or contact support."
        if (result.data?.uploadImage?.errors) errorMessage += ` ${result.data.uploadImage.errors}`
        showToast({
          type: "error",
          content: errorMessage
        })
      }
    })
  }

  // Handle image removal
  const handleImageRemove = () => {
    handleChange("imageUrl", "")
    showToast("Image removed.")
  }

  // Handle multiple images upload
  const handleMultipleImagesUpload = (res) => {
    const files = res.filesUploaded || []

    if (files.length === 0) {
      showToast({
        type: "error",
        content: "No files were uploaded. Please try again."
      })
      return
    }

    // Upload all files in parallel
    const uploadPromises = files.map((file) =>
      uploadImage({
        handle: file.handle,
        description: "hero_image",
        provider: "filestack"
      })
    )

    Promise.all(uploadPromises)
      .then((results) => {
        const successfulUploads = []
        const errors = []

        results.forEach((result, index) => {
          if (result?.data?.uploadImage?.result === "success") {
            successfulUploads.push({
              url: result.data.uploadImage.photoUrl,
              altText: result.data.uploadImage.altText || `Hero image ${index + 1}`
            })
          } else {
            errors.push(`File ${index + 1}: ${result?.data?.uploadImage?.errors || "Upload failed"}`)
          }
        })

        if (successfulUploads.length > 0) {
          const currentImages = formData.settings.images || []
          const newImages = [...currentImages, ...successfulUploads].slice(0, 3) // Limit to 3 images
          handleSettingsChange("images", newImages)

          // Update the main imageUrl to the first image for backward compatibility
          if (successfulUploads.length > 0 && !formData.imageUrl) {
            handleChange("imageUrl", successfulUploads[0].url)
          }

          if (successfulUploads.length === files.length) {
            showToast(
              `${successfulUploads.length} image${successfulUploads.length > 1 ? "s" : ""} uploaded successfully!`
            )
          } else {
            showToast(`${successfulUploads.length} of ${files.length} images uploaded successfully.`)
          }
        }

        if (errors.length > 0) {
          showToast({
            type: "error",
            content: `Some uploads failed: ${errors.join(", ")}`
          })
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error)
        showToast({
          type: "error",
          content: "There was an error uploading your images. Please try again later or contact support."
        })
      })
  }

  const handleRemoveMultipleImage = (index) => {
    const currentImages = formData.settings.images || []
    const newImages = currentImages.filter((_, i) => i !== index)
    handleSettingsChange("images", newImages)

    // If we removed the first image and there are more images, update imageUrl to the new first image
    if (index === 0 && newImages.length > 0) {
      handleChange("imageUrl", newImages[0].url)
    } else if (newImages.length === 0) {
      handleChange("imageUrl", "")
    }

    showToast("Image removed.")
  }

  return (
    <>
      {/* Form Content */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        {currentPage?.isCustom === true && (
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0 border-b border-builder-border-light pb-6">
            <div className="flex w-full items-center justify-between rounded-lg border border-builder-border-light p-4">
              <div className="pr-4">
                <p className="text-sm font-semibold text-builder-primary">Homepage-style hero</p>
                <p className="text-sm text-builder-secondary">
                  When on, this page uses the same hero component as your homepage. When off, it uses the compact hero
                  used on other pages.
                </p>
              </div>
              <Toggle
                checked={formData.settings?.useHomepageHero === true}
                onChange={(checked) => handleSettingsChange("useHomepageHero", checked)}
              />
            </div>
          </div>
        )}

        {/* Layout Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Layout</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">Select a layout design for this section:</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 place-content-start items-start gap-3 text-clip p-0 [flex-flow:wrap]">
            {layoutOptions.map((option) => {
              // Check if this is a shape border layout
              const shapeBorderLayouts = {
                full_image_wave: "wave",
                full_image_point: "point",
                full_image_rounded: "rounded",
                full_image_slant: "slant",
                full_image_point_up_opacity: "point_up_opacity"
              }
              const isShapeBorderLayout = shapeBorderLayouts[option.id]

              // Determine if this option is selected
              const isSelected = isShapeBorderLayout
                ? formData.settings.layout === "full_image" && formData.settings.shapeBorder === isShapeBorderLayout
                : formData.settings.layout === option.id &&
                  (!formData.settings.shapeBorder || formData.settings.shapeBorder === "none")

              return (
                <LayoutOption
                  key={option.id}
                  option={option}
                  isSelected={isSelected}
                  onSelect={(layoutId) => {
                    if (shapeBorderLayouts[layoutId]) {
                      // Set layout to full_image and shapeBorder to the specific type
                      handleSettingsChange("layout", "full_image")
                      handleSettingsChange("shapeBorder", shapeBorderLayouts[layoutId])
                    } else {
                      // Regular layout - clear shape border
                      handleSettingsChange("layout", layoutId)
                      handleSettingsChange("shapeBorder", "none")
                    }
                  }}
                />
              )
            })}
          </div>
        </div>

        {/* Section background color - same as other sections (White / Light / Dark) */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <BackgroundColorSelector
            formData={formData}
            onFormDataChange={setFormData}
            onSettingsChange={handleSettingsChange}
            sectionType="hero"
            showHeaderContent={false}
            theme={practice?.theme}
          />
        </div>

        {/* Headline Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Headline</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">
                Create a powerful headline to capture attention and communicate your core message.
              </p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative h-20 w-full shrink-0 rounded-lg bg-white">
              <textarea
                value={formData.headline}
                onChange={(e) => handleChange("headline", e.target.value)}
                placeholder="Enter your main headline"
                className="box-border flex h-20 w-full resize-none flex-row content-stretch items-start justify-between text-clip rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
                rows={3}
              />
            </div>
          </div>
        </div>
        {/* Sub-headline Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Sub-headline</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">Add a brief sub-headline to expand on your main message.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative h-20 w-full shrink-0 rounded-lg bg-white">
              <textarea
                value={formData.subheadline}
                onChange={(e) => handleChange("subheadline", e.target.value)}
                placeholder="Supporting text for your headline"
                className="box-border flex h-20 w-full resize-none flex-row content-stretch items-start justify-between text-clip rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Call-to-action button Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
              <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
                <p className="block leading-[24px]">Call-to-action button</p>
              </div>
              <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
                <p className="block leading-[20px]">
                  Guide visitors to the next step with a clear call-to-action button.
                </p>
              </div>
            </div>

            {/* CTA Type Selection */}
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-6 p-0">
              <div className="relative box-border flex shrink-0 content-center items-center justify-start gap-3 p-0 [flex-flow:wrap]">
                <input
                  type="radio"
                  name="ctaAction"
                  value="page"
                  checked={formData.settings.ctaAction === "page"}
                  onChange={(e) => handleSettingsChange("ctaAction", e.target.value)}
                  className="relative size-6 shrink-0 accent-builder-accent"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[24px]">Page</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="ctaAction"
                  value="service"
                  checked={formData.settings.ctaAction === "service"}
                  onChange={(e) => handleSettingsChange("ctaAction", e.target.value)}
                  className="relative size-6 shrink-0 accent-builder-accent"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[24px]">Service or Event</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="ctaAction"
                  value="external"
                  checked={formData.settings.ctaAction === "external"}
                  onChange={(e) => handleSettingsChange("ctaAction", e.target.value)}
                  className="relative size-6 shrink-0 accent-builder-accent"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[24px]">External Link</p>
                </div>
              </div>
            </div>
          </div>

          {/* Button Text */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-start justify-start gap-1 p-0">
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-builder-primary">
                <p className="block whitespace-pre leading-[20px]">Button text</p>
              </div>
            </div>
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <input
                type="text"
                value={formData.settings.ctaText || "Learn More"}
                onChange={(e) => handleSettingsChange("ctaText", e.target.value)}
                placeholder="Learn More"
                className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
              />
            </div>
          </div>

          {/* Target Selection */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-start justify-start gap-1 p-0">
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-builder-primary">
                <p className="block whitespace-pre leading-[20px]">
                  {formData.settings.ctaAction === "page"
                    ? "Page"
                    : formData.settings.ctaAction === "service"
                    ? "Service or Event"
                    : "External URL"}
                </p>
              </div>
            </div>
            {formData.settings.ctaAction === "page" && (
              <div className="relative w-full shrink-0 rounded-lg bg-white">
                <select
                  value={formData.settings.ctaTarget || ""}
                  onChange={(e) => handleSettingsChange("ctaTarget", e.target.value)}
                  className="relative box-border flex w-full appearance-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none">
                  <option value="">Select a page</option>
                  {pages?.map((page) => (
                    <option key={page.slug} value={page.slug}>
                      {page.title}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                  <svg
                    className="size-4 text-builder-secondary opacity-65"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            )}
            {formData.settings.ctaAction === "service" && (
              <>
                <div className="relative w-full shrink-0 rounded-lg bg-white">
                  <select
                    value={normalizeCtaTargetForSelect(formData.settings.ctaTarget)}
                    onChange={(e) => handleSettingsChange("ctaTarget", e.target.value)}
                    className="relative box-border flex w-full appearance-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none">
                    <option value="">Select</option>
                    <optgroup label="Services">
                      {(practiceData?.services || [])
                        .filter((s) => s.public === true && !s.package)
                        .map((service) => (
                          <option key={`service-${service.id}`} value={formatServiceTargetValue(service.id)}>
                            {service.name}
                          </option>
                        ))}
                    </optgroup>
                    <optgroup label="Packages">
                      {(practiceData?.services || [])
                        .filter((s) => s.public === true && s.package === true)
                        .map((pkg) => (
                          <option key={`package-${pkg.id}`} value={formatServiceTargetValue(pkg.id)}>
                            {pkg.name}
                          </option>
                        ))}
                    </optgroup>
                    <optgroup label="Events">
                      {(practiceData?.bookableEventTypes || []).map((eventType) => (
                        <option key={`event-type-${eventType.id}`} value={formatEventTargetValue(eventType.id)}>
                          {eventType.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                    <svg
                      className="size-4 text-builder-secondary opacity-65"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-builder-secondary">
                  <p className="block whitespace-pre leading-[19.5px]">
                    Go to your <span className="cursor-pointer text-builder-accent">Services</span> to add additional
                    options.
                  </p>
                </div>
              </>
            )}
            {formData.settings.ctaAction === "external" && (
              <>
                <div className="relative w-full shrink-0 rounded-lg bg-white">
                  <input
                    type="url"
                    value={formData.settings.ctaTarget || ""}
                    onChange={(e) => handleSettingsChange("ctaTarget", e.target.value)}
                    placeholder="https://example.com"
                    className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
                  />
                </div>
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-builder-secondary">
                  <p className="block whitespace-pre leading-[19.5px]">
                    Enter a complete URL starting with http:// or https://
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* CTA Button Alignment Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">CTA Button Alignment</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">
                Choose how to align the call-to-action button within its container.
              </p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-6 p-0">
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
              <input
                type="radio"
                name="ctaAlign"
                value="left"
                checked={formData.settings.ctaAlign === "left"}
                onChange={(e) => handleSettingsChange("ctaAlign", e.target.value)}
                className="relative size-6 shrink-0 accent-builder-accent"
              />
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
                <p className="block whitespace-pre leading-[24px]">Left</p>
              </div>
            </div>
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
              <input
                type="radio"
                name="ctaAlign"
                value="center"
                checked={formData.settings.ctaAlign === "center"}
                onChange={(e) => handleSettingsChange("ctaAlign", e.target.value)}
                className="relative size-6 shrink-0 accent-builder-accent"
              />
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
                <p className="block whitespace-pre leading-[24px]">Center</p>
              </div>
            </div>
            <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
              <input
                type="radio"
                name="ctaAlign"
                value="right"
                checked={formData.settings.ctaAlign === "right"}
                onChange={(e) => handleSettingsChange("ctaAlign", e.target.value)}
                className="relative size-6 shrink-0 accent-builder-accent"
              />
              <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
                <p className="block whitespace-pre leading-[24px]">Right</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button Spacing Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">CTA Button Spacing</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">
                Control the vertical spacing between the subheadline text and the call-to-action button.
              </p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
              <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-between p-0">
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[20px]">Top Margin</p>
                </div>
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[0] text-builder-secondary">
                  <p className="block whitespace-pre leading-[20px]">{formData.settings.ctaTopMargin || 24}px</p>
                </div>
              </div>
              <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="range"
                  min="0"
                  max="80"
                  step="4"
                  value={formData.settings.ctaTopMargin || 24}
                  onChange={(e) => handleSettingsChange("ctaTopMargin", parseInt(e.target.value))}
                  className="relative flex-1 shrink-0 accent-builder-accent"
                />
              </div>
              <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-between p-0">
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[12px] font-normal leading-[0] text-builder-secondary">
                  <p className="block whitespace-pre leading-[18px]">0px</p>
                </div>
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[12px] font-normal leading-[0] text-builder-secondary">
                  <p className="block whitespace-pre leading-[18px]">80px</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Background Image Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Background image</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">
                Select a striking image that sets the tone and draws visitors into your website.
              </p>
            </div>
          </div>

          {/* Image Upload Area */}
          {formData.imageUrl ? (
            <div className="mb-6 flex items-center justify-center">
              <div className="flex items-center gap-4">
                <img src={formData.imageUrl} alt="Hero background image" className="size-16 rounded-lg object-cover" />
                <div className="flex flex-col gap-2">
                  <ImagePickerV2
                    practice={practice}
                    onImageSelect={handleImageSelect}
                    fetching={uploadFetching}
                    showCurateTab={true}
                    currentImageUrl={formData.imageUrl}
                    trigger={
                      <button className="font-bold text-builder-accent hover:underline" disabled={uploadFetching}>
                        Edit image
                      </button>
                    }
                  />
                  <button
                    className="font-bold text-red-500 hover:underline"
                    onClick={handleImageRemove}
                    disabled={uploadFetching}>
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <ImagePickerV2
              practice={practice}
              onImageSelect={handleImageSelect}
              fetching={uploadFetching}
              showCurateTab={true}
              currentImageUrl={formData.imageUrl}
              trigger={
                <div className="relative box-border flex w-full shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-builder-border bg-white p-6 transition-colors hover:border-builder-accent">
                  <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-builder-bg-light">
                    <svg className="size-5 text-builder-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <div className="relative shrink-0 text-nowrap text-center font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-accent">
                    <p className="whitespace-pre leading-[24px]">
                      <span className="font-['Manrope:Bold',_sans-serif] font-bold">upload an image</span>
                    </p>
                  </div>
                  <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-builder-secondary">
                    <p className="block whitespace-pre leading-[19.5px]">Supported formats: PNG, JPG, SVG</p>
                  </div>
                </div>
              }
            />
          )}
        </div>

        {/* Multiple Images Section - Only show for layouts that support multiple images */}
        {(formData.settings.layout === "scattered_images" || formData.settings.layout === "overlap_double_image") && (
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
              <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
                <p className="block leading-[24px]">Multiple Images</p>
              </div>
              <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
                <p className="block leading-[20px]">
                  Upload up to 3 images for layouts that support multiple images. For single-image layouts, the first
                  image will be used.
                </p>
              </div>
            </div>

            {/* Multiple Images Gallery */}
            {formData.settings.images && formData.settings.images.length > 0 ? (
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
                <div className="relative box-border flex w-full shrink-0 place-content-start items-start gap-4 text-clip p-0 [flex-flow:wrap]">
                  {formData.settings.images.map((image, index) => (
                    <div key={index} className="relative flex flex-col items-center gap-2">
                      <div className="relative">
                        <img
                          src={image.url}
                          alt={image.altText || `Hero image ${index + 1}`}
                          className="size-20 rounded-lg object-cover"
                        />
                        <button
                          type="button"
                          className="absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
                          onClick={() => handleRemoveMultipleImage(index)}
                          disabled={uploadFetching}>
                          <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                      <span className="text-xs text-gray-500">Image {index + 1}</span>
                    </div>
                  ))}
                </div>
                {formData.settings.images.length < 3 && (
                  <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-center p-0">
                    <FilePicker
                      as="button"
                      className="font-bold text-builder-accent"
                      onUploadDone={handleMultipleImagesUpload}
                      disabled={uploadFetching}
                      transformations={null}
                      accept="image/*"
                      multiple={true}>
                      Add more images
                    </FilePicker>
                  </div>
                )}
              </div>
            ) : (
              <FilePicker
                as="button"
                onUploadDone={handleMultipleImagesUpload}
                disabled={uploadFetching}
                transformations={null}
                accept="image/*"
                multiple={true}
                className="relative box-border flex w-full shrink-0 flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-builder-border bg-white px-[149px] py-10 transition-colors hover:border-builder-accent">
                <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-builder-bg-light">
                  <svg className="size-5 text-builder-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <div className="relative shrink-0 text-nowrap text-center font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-accent">
                  <p className="whitespace-pre leading-[24px]">
                    <span className="font-['Manrope:Bold',_sans-serif] font-bold">upload images</span>
                  </p>
                </div>
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-builder-secondary">
                  <p className="block whitespace-pre leading-[19.5px]">
                    Select multiple images at once. Supported formats: PNG, JPG, SVG
                  </p>
                </div>
              </FilePicker>
            )}
          </div>
        )}

        {/* Client reviews Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Client reviews</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">Show your overall star rating from clients.</p>
            </div>
          </div>
          <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
            <Toggle
              checked={formData.settings.showReviews || false}
              onChange={(checked) => handleSettingsChange("showReviews", checked)}
            />
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
              <p className="block whitespace-pre leading-[24px]">Include review ratings</p>
            </div>
          </div>
        </div>

        {/* Text Alignment Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Text Alignment</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">
                Choose how to align your heading and subheading text independently.
              </p>
            </div>
          </div>

          {/* Heading Alignment */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
              <p className="block leading-[20px]">Heading Alignment</p>
            </div>
            <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-6 p-0">
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="headingAlign"
                  value="left"
                  checked={formData.settings.headingAlign === "left"}
                  onChange={(e) => handleSettingsChange("headingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-builder-accent"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[24px]">Left</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="headingAlign"
                  value="center"
                  checked={formData.settings.headingAlign === "center"}
                  onChange={(e) => handleSettingsChange("headingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-builder-accent"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[24px]">Center</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="headingAlign"
                  value="right"
                  checked={formData.settings.headingAlign === "right"}
                  onChange={(e) => handleSettingsChange("headingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-builder-accent"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[24px]">Right</p>
                </div>
              </div>
            </div>
          </div>

          {/* Subheading Alignment */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
              <p className="block leading-[20px]">Subheading Alignment</p>
            </div>
            <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-6 p-0">
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="subheadingAlign"
                  value="left"
                  checked={formData.settings.subheadingAlign === "left"}
                  onChange={(e) => handleSettingsChange("subheadingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-builder-accent"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[24px]">Left</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="subheadingAlign"
                  value="center"
                  checked={formData.settings.subheadingAlign === "center"}
                  onChange={(e) => handleSettingsChange("subheadingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-builder-accent"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[24px]">Center</p>
                </div>
              </div>
              <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                <input
                  type="radio"
                  name="subheadingAlign"
                  value="right"
                  checked={formData.settings.subheadingAlign === "right"}
                  onChange={(e) => handleSettingsChange("subheadingAlign", e.target.value)}
                  className="relative size-6 shrink-0 accent-builder-accent"
                />
                <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-primary">
                  <p className="block whitespace-pre leading-[24px]">Right</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Font Settings Section */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 border-b border-builder-border-light px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary">
              <p className="block leading-[24px]">Typography</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-builder-secondary">
              <p className="block leading-[20px]">Customize the fonts and styling of your text content.</p>
            </div>
          </div>

          {/* Headline Font Settings */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
              <p className="block leading-[20px]">Headline</p>
            </div>
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[12px] font-bold text-builder-primary">
                  <p className="block leading-[18px]">Font Size</p>
                </div>
                <select
                  value={formData.settings.headlineSize || "40px"}
                  onChange={(e) => handleSettingsChange("headlineSize", e.target.value)}
                  className="relative box-border flex w-full appearance-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none">
                  <option value="32px">32px</option>
                  <option value="40px">40px</option>
                  <option value="48px">48px</option>
                  <option value="56px">56px</option>
                  <option value="64px">64px</option>
                  <option value="72px">72px</option>
                </select>
              </div>
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[12px] font-bold text-builder-primary">
                  <p className="block leading-[18px]">Text Color</p>
                </div>
                <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                  <input
                    type="color"
                    value={formData.settings.headlineColor || "#FFFFFF"}
                    onChange={(e) => handleSettingsChange("headlineColor", e.target.value)}
                    className="relative h-10 w-16 shrink-0 cursor-pointer rounded border border-builder-border"
                  />
                  <input
                    type="text"
                    value={formData.settings.headlineColor || "#FFFFFF"}
                    onChange={(e) => handleSettingsChange("headlineColor", e.target.value)}
                    placeholder="#FFFFFF"
                    className="box-border flex h-10 w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Subheadline Font Settings */}
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-builder-primary">
              <p className="block leading-[20px]">Subheadline</p>
            </div>
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-4 p-0">
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[12px] font-bold text-builder-primary">
                  <p className="block leading-[18px]">Font Size</p>
                </div>
                <select
                  value={formData.settings.subheadlineSize || "20px"}
                  onChange={(e) => handleSettingsChange("subheadlineSize", e.target.value)}
                  className="relative box-border flex w-full appearance-none flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border bg-white px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none">
                  <option value="14px">14px</option>
                  <option value="16px">16px</option>
                  <option value="18px">18px</option>
                  <option value="20px">20px</option>
                  <option value="24px">24px</option>
                  <option value="28px">28px</option>
                </select>
              </div>
              <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[12px] font-bold text-builder-primary">
                  <p className="block leading-[18px]">Text Color</p>
                </div>
                <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
                  <input
                    type="color"
                    value={formData.settings.subheadlineColor || "#FFFFFF"}
                    onChange={(e) => handleSettingsChange("subheadlineColor", e.target.value)}
                    className="relative h-10 w-16 shrink-0 cursor-pointer rounded border border-builder-border"
                  />
                  <input
                    type="text"
                    value={formData.settings.subheadlineColor || "#FFFFFF"}
                    onChange={(e) => handleSettingsChange("subheadlineColor", e.target.value)}
                    placeholder="#FFFFFF"
                    className="box-border flex h-10 w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-builder-border px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary focus:border-builder-accent focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isEditing && !canDelete && (
        <div className="px-8 pb-4 text-sm text-gray-500">
          This section is required on default pages and can&apos;t be deleted.
        </div>
      )}

      {/* Footer Buttons */}
      <EditorFooter
        onDelete={canDelete ? onDelete : undefined}
        onCancel={handleCancelClick}
        onSave={handleSubmit}
        showDelete={isEditing && canDelete}
      />
    </>
  )
}

export default HeroEditor
